import {
  AnonCompany,
  AnonEmployee,
} from 'features/productGrowth/PostSignup/GustoIntegration/types';
import { FormValues as BusinessDetailsFormValues } from 'features/productGrowth/PostSignup/Inception/components/ReviewUpdate/ReviewUpdate';
import { BulkAddTeamMember, FieldCell } from 'features/productGrowth/types';

export const transformAnonBusinessDetails = (
  company: AnonCompany
): BusinessDetailsFormValues | undefined => {
  // Gracefully handle empty input
  if (!company) return undefined;

  const parsedBusinessLegalName = company.parsed_business_legal_name ?? '';
  const parsedAddress = company.parsed_address;
  const parsedType = company.parsed_type ?? '';
  const parsedEin = company.parsed_ein ?? '';
  const parsedSignatory = company.parsed_signatory;

  const address = {
    line1: parsedAddress?.line1 ?? '',
    line2: parsedAddress?.line2 ?? '',
    city: parsedAddress?.city ?? '',
    state: parsedAddress?.state ?? '',
    postal_code: parsedAddress?.postal_code ?? '',
  };

  // Build object that matches ReviewUpdate.FormValues. Any fields we don't have
  // data for are intentionally left undefined so downstream validation can run.
  const data: BusinessDetailsFormValues = {
    // Contact / signer info
    title: parsedSignatory?.title ?? '',
    first_name: parsedSignatory?.first_name ?? '',
    last_name: parsedSignatory?.last_name ?? '',
    full_name: parsedSignatory?.full_name ?? '',
    email_address: parsedSignatory?.email_address ?? '',
    personal_phone_number: parsedSignatory?.personal_phone_number ?? '',

    // Business info
    business_type: parsedType,
    business_legal_name: parsedBusinessLegalName,
    federal_employer_identification_number: parsedEin,

    // Address (legal)
    business_legal_address_line1: address.line1,
    business_legal_address_line2: address.line2,
    business_legal_address_city: address.city,
    business_legal_address_state: address.state,
    business_legal_address_postal_code: address.postal_code,

    // Workplace address (same as legal)
    workplace_legal_address_line1: address.line1,
    workplace_legal_address_line2: address.line2,
    workplace_legal_address_city: address.city,
    workplace_legal_address_state: address.state,
    workplace_legal_address_postal_code: address.postal_code,
  };

  return data;
};

export const makeTeamMemberCell = <T>(
  value: T,
  columnName: string,
  fieldName: string = columnName
): FieldCell<T> => ({
  value,
  columnName,
  fieldName,
  changed: true,
});

export const transformAnonTeamMembers = (
  employees: AnonEmployee[]
): BulkAddTeamMember[] => {
  if (!employees?.length) return [];

  const levelOrder = {
    'General Manager': 1,
    Manager: 2,
    Employee: 3,
  };

  // Process employees to create team member objects
  const teamMembers = employees
    .filter(
      employee =>
        employee?.employmentDetails?.status?.employmentStatus?.toLowerCase?.() !==
        'terminated'
    )
    .map(employee => {
      // Safely unpack nested objects with optional chaining & defaults
      const personalInformation = employee?.personalInformation ?? {};
      const contactInformation = employee?.contactInformation ?? {};
      const employmentDetails = employee?.employmentDetails ?? {};
      const compensation = employee?.compensation ?? {};

      // Personal details
      const firstName = personalInformation?.name?.firstName ?? '';
      const lastName = personalInformation?.name?.lastName ?? '';

      // Contact details
      const workEmail = (contactInformation?.email?.work || '').trim();
      const personalEmail = (contactInformation?.email?.personal || '').trim();
      const email = workEmail || personalEmail;

      const phone = contactInformation?.phone?.primary ?? '';

      // Job / role details
      const jobTitle = employmentDetails?.position?.jobTitle ?? '';
      const level = ['General Manager', 'Manager'].includes(jobTitle)
        ? jobTitle
        : 'Employee';
      const role = jobTitle || 'Employee';

      // Compensation details
      const { amount = 0, unit = 'hour' } = compensation?.primary ?? {};
      const wageRate = Number(amount).toFixed(2);

      const normalizedUnit = String(unit).trim().toLowerCase();
      const isHourly = [
        'hour',
        'hours',
        'hr',
        'hrs',
        'hourly',
        'per hour',
      ].some(term => normalizedUnit.includes(term));
      const wageType = isHourly ? 0 : 1; // 0: hourly | 1: salary

      // Values ready for employeesBulkAdd API
      return {
        first_name: makeTeamMemberCell(firstName, 'first_name'),
        last_name: makeTeamMemberCell(lastName, 'last_name'),
        email: makeTeamMemberCell(email, 'email'),
        phone: makeTeamMemberCell(phone, 'phone'),
        level: makeTeamMemberCell(level, 'level'),
        role: makeTeamMemberCell([role], 'role'),
        wage_rate: makeTeamMemberCell(wageRate, 'wage_rate'),
        wage_type: { value: wageType, columnName: 'wage_type' },
        // Properties to help with sorting
        _sortLevel: level,
        _sortName: `${firstName} ${lastName}`.trim().toLowerCase(),
      };
    });

  // Sort by access level (General Manager first, Manager second, Employee last)
  // and then by name within the same access level
  const sortedTeamMembers: BulkAddTeamMember[] = [...teamMembers]
    .sort((a, b) => {
      // Get the level values for comparison
      const levelA = a._sortLevel;
      const levelB = b._sortLevel;

      // Check if the levels are valid keys in levelOrder
      const isValidLevelA = levelA in levelOrder;
      const isValidLevelB = levelB in levelOrder;

      // Handle cases where one or both levels are not valid
      if (!isValidLevelA && !isValidLevelB) {
        // If both are invalid, fall back to name comparison
        return a._sortName.localeCompare(b._sortName);
      } else if (!isValidLevelA) {
        // Invalid levels go after valid ones
        return 1;
      } else if (!isValidLevelB) {
        return -1;
      }

      // Both levels are valid, use type assertion safely now
      const orderA = levelOrder[levelA as keyof typeof levelOrder];
      const orderB = levelOrder[levelB as keyof typeof levelOrder];

      // First sort by level
      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // If same level, sort by name
      return a._sortName.localeCompare(b._sortName);
    })
    // Remove the temporary sorting properties before returning
    .map(({ _sortLevel, _sortName, ...member }, idx) => ({
      ...member,
      row_id: idx,
    }));

  return sortedTeamMembers;
};
